import { ComponentProps, FC, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Box, Button, Heading, Text, VStack } from '@bonusly/design-system';

import { UserSpotlightCardShell } from './user-spotlight-card-shell';
import { getUserSpotlightCtaCopyNodes, UserSpotlightCtaCopyNode, UserSpotlightCtaPersona } from './user-spotlight-cta-persona';

interface UserSpotlightCallToActionCardProps {
	persona: UserSpotlightCtaPersona;
	userDisplayFirstName?: string;
	onAddToRecognition?: () => void;
	onThankTopGiver?: () => void;
	onRecognizeTopGiver?: () => void;
	recognizeTargetDisplayName?: string;
}

export const UserSpotlightCallToActionCard: FC<UserSpotlightCallToActionCardProps> = ({
	persona,
	userDisplayFirstName,
	onAddToRecognition,
	onThankTopGiver,
	onRecognizeTopGiver,
	recognizeTargetDisplayName,
}) => {
	const { t } = useTranslation('spotlight');

	const isManager = persona === 'manager';
	const isRecipient = persona === 'recipient';
	const isViewer = !isManager && !isRecipient;

	const renderCopyNode = (node: UserSpotlightCtaCopyNode): ReactNode => {
		switch (node.kind) {
			case 't':
				return t(node.i18nKey);
			case 'trans':
				return <Trans i18nKey={node.i18nKey} ns="spotlight" values={node.values} />;
		}
	};

	const { headingNode, bodyNode, footerNode } = getUserSpotlightCtaCopyNodes({ persona, userDisplayFirstName });

	const ctaButtonProps = {
		variant: 'solid',
		fontSize: 'lg',
		colorScheme: 'secondary',
		pt: 5,
		pb: 5,
		w: 'full',
	} satisfies Partial<ComponentProps<typeof Button>>;

	return (
		<UserSpotlightCardShell innerPadding={{ base: 12, md: 16 }}>
			<Box textAlign="start">
				<VStack spacing={5}>
					<Heading as="h2" alignSelf="start" fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color="text.default">
						{renderCopyNode(headingNode)}
					</Heading>
					<Text alignSelf="start" fontSize={{ base: 'md', md: '2xl' }} color="text.default">
						{renderCopyNode(bodyNode)}
					</Text>

					{isManager && onAddToRecognition && (
						<Button onClick={onAddToRecognition} {...ctaButtonProps}>
							{t('callToAction.persona.manager.addToRecognitionButton')}
						</Button>
					)}

					{isRecipient && onThankTopGiver && (
						<Button onClick={onThankTopGiver} {...ctaButtonProps}>
							{t('callToAction.persona.recipient.thankButton')}
						</Button>
					)}

					{isViewer && onRecognizeTopGiver && (
						<Button onClick={onRecognizeTopGiver} {...ctaButtonProps}>
							{recognizeTargetDisplayName
								? t('callToAction.persona.viewer.recognizeButtonWithName', { name: recognizeTargetDisplayName })
								: t('callToAction.persona.viewer.recognizeButton')}
						</Button>
					)}

					<Text alignSelf="start" fontSize={{ base: 'sm', md: 'md' }} color="text.softest">
						{renderCopyNode(footerNode)}
					</Text>
				</VStack>
			</Box>
		</UserSpotlightCardShell>
	);
};
